import get from 'lodash-es/get'
import React from 'react'

import { StatusTag } from '@components/common/StatusTag'
import { TableCell } from '@components/common/TableCell'
import { TableProductsList } from '@components/common/TableProductsList'
import { TableItemDate } from '@components/utils/TableItemDate'
import type { TableHeaders } from '@fourthwall/components'
import { Icon, PriceString, ProfitString, Tooltip } from '@fourthwall/components'
import { QuestionMarkIcon } from '@fourthwall/icons'
import { getNumberOfProducts } from '@utils/products'

import type { Order } from '../../../../../../__models__'
import { getBackerName } from './utils'

export const getProductOrdersTableHeaders = ({
  hideProfitColumn,
  timezoneAbbreviation,
}: {
  hideProfitColumn: boolean
  timezoneAbbreviation: string
}): TableHeaders<Order> => {
  const headers: TableHeaders<Order> = [
    {
      id: 'createdAt',
      label: `Date (${timezoneAbbreviation})`,
      render: ({ createdAt }) => (
        <div>
          <TableItemDate date={createdAt} />
        </div>
      ),
    },
    {
      id: 'products',
      label: ' ',
      render: ({ offers, donation, offersCancelled }) => (
        <TableProductsList
          offers={offers}
          offersCancelled={offersCancelled}
          donation={donation}
          quantity={getNumberOfProducts(offers, donation)}
        />
      ),
    },
    {
      id: 'shipping.address.firstName',
      label: 'Supporter',
      render: (order) => <TableCell mainContent={getBackerName(order)} $ellipsis />,
      width: 0,
      minWidth: 220,
    },
    {
      id: 'status',
      label: 'Status',
      render: ({ status }) => {
        return <StatusTag status={status} />
      },
      width: 2,
    },
    {
      id: 'totalPrice.value',
      label: 'Total',
      render: ({ totalPrice }) => (
        <TableCell mainContent={<PriceString value={get(totalPrice, 'value')} />} />
      ),
    },
    {
      id: 'profit',
      hidden: hideProfitColumn,
      label: (
        <Tooltip
          reference={
            <span
              style={{
                display: 'grid',
                gridAutoFlow: 'column',
                alignItems: 'middle',
                gap: 3,
              }}
            >
              Profit <Icon component={QuestionMarkIcon} height={12} />
            </span>
          }
        >
          <span style={{ textTransform: 'none' }}>
            If the order contains MOQ screen printed products, profit values are estimated. The
            final profit values will be available within earnings history.
          </span>
        </Tooltip>
      ),
      render: ({ profit }) => (
        <TableCell mainContent={profit ? <ProfitString value={profit.value} /> : <>&ndash;</>} />
      ),
    },
  ]

  return headers.filter((column) => !column.hidden)
}
